import { useCallback, useEffect, useRef, useState } from 'react';
import { vault } from '../sync/vault.js';
import { index } from '../worker/client.js';
import { useIndexRev } from './hooks.js';
import { frontmatterSplice } from './fm.js';

interface PropRow {
  key: string;
  value: unknown;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Typed frontmatter editor (Phase 2). Reads the indexed frontmatter, writes
 * by splicing ONLY the frontmatter block of the note's Y.Text (one transact). */
export function PropertiesPanel({ noteId }: { noteId: string }) {
  const rev = useIndexRev();
  const [open, setOpen] = useState(true);
  const [rows, setRows] = useState<PropRow[]>([]);
  const dirty = useRef(false);

  useEffect(() => {
    dirty.current = false;
    setRows([]);
  }, [noteId]);

  useEffect(() => {
    let stale = false;
    void index.note(noteId).then((entry) => {
      if (stale || dirty.current) return;
      const fm = entry?.frontmatter ?? {};
      setRows(Object.entries(fm).map(([key, value]) => ({ key, value })));
    });
    return () => {
      stale = true;
    };
  }, [noteId, rev]);

  const write = useCallback(
    (next: PropRow[]) => {
      const props: Record<string, unknown> = {};
      for (const r of next) {
        const k = r.key.trim();
        if (!k) continue;
        props[k] = r.value;
      }
      const yt = vault.ytext(noteId);
      const doc = vault.noteDoc(noteId);
      if (!yt || !doc) return;
      const { deleteLen, insert } = frontmatterSplice(yt.toString(), props);
      doc.transact(() => {
        if (deleteLen > 0) yt.delete(0, deleteLen);
        if (insert) yt.insert(0, insert);
      });
      dirty.current = false;
    },
    [noteId],
  );

  const setAnd = (next: PropRow[], commitNow: boolean) => {
    dirty.current = true;
    setRows(next);
    if (commitNow) write(next);
  };

  const update = (i: number, patch: Partial<PropRow>, commitNow = false) =>
    setAnd(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)), commitNow);

  const removeRow = (i: number) => setAnd(rows.filter((_, j) => j !== i), true);

  const addRow = () => {
    setAnd([...rows, { key: '', value: '' }], false);
  };

  const commit = () => write(rows);

  return (
    <section className="panel-section">
      <button className="panel-header collapsible" onClick={() => setOpen(!open)}>
        <span className={`tree-chevron${open ? ' open' : ''}`}>▸</span>
        <span className="panel-title">Properties</span>
        {rows.length > 0 && <span className="count-badge">{rows.length}</span>}
      </button>
      {open && (
        <div className="props-body">
          {rows.length === 0 && <div className="pane-empty">No properties.</div>}
          {rows.map((r, i) => (
            <div className="prop-row" key={i}>
              <input
                className="prop-key"
                value={r.key}
                placeholder="key"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                onChange={(e) => update(i, { key: e.target.value })}
                onBlur={commit}
              />
              <PropValue
                value={r.value}
                onChange={(v) => update(i, { value: v })}
                onCommitValue={(v) => update(i, { value: v }, true)}
                onBlur={commit}
              />
              <button
                className="prop-remove"
                title="Remove property"
                onClick={() => removeRow(i)}
              >
                ×
              </button>
            </div>
          ))}
          <button className="add-prop" onClick={addRow}>
            ＋ Add property
          </button>
        </div>
      )}
    </section>
  );
}

function PropValue({
  value,
  onChange,
  onCommitValue,
  onBlur,
}: {
  value: unknown;
  onChange: (v: unknown) => void;
  onCommitValue: (v: unknown) => void;
  onBlur: () => void;
}) {
  if (typeof value === 'boolean') {
    return (
      <span className="prop-checkwrap">
        <input
          type="checkbox"
          className="prop-check"
          checked={value}
          onChange={(e) => onCommitValue(e.target.checked)}
        />
      </span>
    );
  }
  if (typeof value === 'number') {
    return (
      <input
        type="number"
        className="prop-input"
        value={Number.isFinite(value) ? value : ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        onBlur={onBlur}
      />
    );
  }
  if (Array.isArray(value)) {
    return (
      <ChipEditor items={value.map((x) => String(x))} onCommit={(items) => onCommitValue(items)} />
    );
  }
  const s = value == null ? '' : String(value);
  if (DATE_RE.test(s)) {
    return (
      <input
        type="date"
        className="prop-input"
        value={s}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
    );
  }
  return (
    <input
      className="prop-input"
      value={s}
      placeholder="value"
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
    />
  );
}

function ChipEditor({
  items,
  onCommit,
}: {
  items: string[];
  onCommit: (items: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    setDraft('');
    if (v) onCommit([...items, v]);
  };
  return (
    <div className="chips">
      {items.map((it, i) => (
        <span className="chip" key={`${it}:${i}`}>
          {it}
          <button
            className="chip-x"
            title={`Remove ${it}`}
            onClick={() => onCommit(items.filter((_, j) => j !== i))}
          >
            ×
          </button>
        </span>
      ))}
      <input
        className="chip-input"
        value={draft}
        placeholder="+"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
      />
    </div>
  );
}
